import type { NavigationGuardWithThis, RouteLocationRaw } from 'vue-router'
import { useSessionStore } from '@/apps/session/stores/session'
import type { OrgPermission } from '@/types/permissions'

/**
 * Declarative permission requirements attached to a route via `meta.requires`, in `base-auth`'s
 * `<domain>.<resource>.<action>` spelling and typed by the generated catalogue: a key that does
 * not exist, or is held at another scope, does not type-check. All specified constraints must
 * pass (AND).
 *
 * **Permissions, never role names.** The list checked is the caller's effective set in the
 * active organization, as `GET /api/app/access` answers it — the set the server checks every
 * request against — so a role an organization defined this morning opens exactly the screens
 * its permissions open.
 */
export interface RequiresMeta {
  /** Permissions the caller must hold (all of them). */
  permissions?: OrgPermission | OrgPermission[]
  /** Permissions of which the caller must hold at least one. */
  anyPermissions?: OrgPermission[]
}

declare module 'vue-router' {
  interface RouteMeta {
    requires?: RequiresMeta
  }
}

export interface CreatePermissionGuardOptions {
  /** Where a refused navigation goes, given the organization. Defaults to its home. */
  fallback?: (organizationId: string) => RouteLocationRaw
}

/**
 * Whether `can` meets `requires` — the guard's rule, exported so that a menu leaving out the
 * screens a caller cannot open asks the same question the navigation would.
 */
export function meetsRequirements(can: (permission: OrgPermission) => boolean, requires: RequiresMeta): boolean {
  const all = requires.permissions === undefined ? [] : [requires.permissions].flat()
  const any = requires.anyPermissions
  return all.every(can) && (any === undefined || any.some(can))
}

/**
 * Registered after the session guard, which has already resolved the organization the URL
 * names and read what the caller may do there — so this reads a store and makes no request.
 */
export function createPermissionGuard(
  opts: CreatePermissionGuardOptions = {},
): NavigationGuardWithThis<undefined> {
  const fallback =
    opts.fallback ??
    ((organizationId: string): RouteLocationRaw => ({ name: 'home', params: { organizationId } }))

  return (to) => {
    const requires = to.meta.requires
    const orgId = to.params.organizationId
    if (!requires || typeof orgId !== 'string' || orgId === '') return true
    const session = useSessionStore()
    return meetsRequirements(session.can, requires) ? true : fallback(orgId)
  }
}
